import React, { useState, useEffect } from 'react';
import {
  Database,
  Cloud,
  RefreshCw,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Trash2,
  X,
  Server,
  Activity,
  Layers,
  HardDrive,
  Users,
  ShieldCheck,
  Check,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { formatINR } from '../../utils/watermark';

interface BackendStorageManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BackendStorageManagerModal: React.FC<BackendStorageManagerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    accounts,
    recoveries,
    ptps,
    visits,
    countAllocations,
    pullSharedMasterData,
    pushSharedMasterData,
    fetchServerStats,
    serverStats,
    firestoreSpeedStats,
    exportAllBackendData,
    lastAutoSyncTime,
  } = useSRMS();

  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  /**
   * The allocation total, counted on the server.
   *
   * The whole collection used to be downloaded on every sign-in by every
   * administrator -- one record per account -- so that this one number could
   * be printed. It is now asked for as a count: one read, when this screen is
   * opened. Same number.
   *
   * `null` while it is being fetched; a negative value means the count could
   * not be read.
   */
  const [allocationCount, setAllocationCount] = useState<number | null>(null);

  const loadBackendInfo = async () => {
    setIsLoadingStats(true);
    try {
      await fetchServerStats();
      const res = await fetch('/api/backend/diagnostics');
      if (res.ok) {
        const d = await res.json();
        setDiagnostics(d);
      }
    } catch (err) {
      console.warn('Failed to load backend diagnostics:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadBackendInfo();
      void countAllocations().then(setAllocationCount);
    }
  }, [isOpen]);

  const handlePullData = async () => {
    setIsSyncing(true);
    setSyncStatus('Pulling latest database from backend server...');
    try {
      const res = await pullSharedMasterData();
      await loadBackendInfo();
      setSyncStatus(res.message || 'Successfully synchronized from backend server.');
    } catch (e: any) {
      setSyncStatus(`Error pulling data: ${e?.message || 'Unknown error'}`);
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncStatus(null), 4000);
    }
  };

  const handlePushData = async () => {
    setIsSyncing(true);
    setSyncStatus('Pushing active workspace to backend master storage...');
    try {
      const res = await pushSharedMasterData();
      await loadBackendInfo();
      setSyncStatus(res.message || 'Successfully saved to central backend database.');
    } catch (e: any) {
      setSyncStatus(`Error pushing data: ${e?.message || 'Unknown error'}`);
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncStatus(null), 4000);
    }
  };

  const handleClearBrowserCacheOnly = () => {
    if (
      window.confirm(
        'This will clear heavy local browser cache (localStorage) and re-sync fresh from the Backend Server. Central server data will NOT be deleted. Proceed?'
      )
    ) {
      try {
        localStorage.removeItem('srms_persisted_accounts');
        localStorage.removeItem('srms_account_storage_cleared_user');
        localStorage.removeItem('srms_persisted_allocations');
        localStorage.removeItem('srms_persisted_allochistories');
      } catch {}
      handlePullData();
    }
  };

  if (!isOpen) return null;

  // The loaded accounts are the live figures and win.
  //
  // `serverStats` used to take precedence, and it is only refreshed by this
  // modal's own pull button -- nothing invalidates it when a collection is
  // recorded. So after collecting money, these totals kept showing the old
  // overdue figure until someone happened to pull again. The server snapshot
  // is now only a fallback for when no accounts are loaded at all.
  const haveAccounts = accounts.length > 0;
  const totalAccountsCount =
    diagnostics?.counts?.accounts ?? (haveAccounts ? accounts.length : serverStats?.totalAccounts ?? 0);
  const totalOutstanding = haveAccounts
    ? accounts.reduce((s, a) => s + (a.outstandingAmount || 0), 0)
    : serverStats?.totalOutstanding ?? 0;
  const totalOverdue = haveAccounts
    ? accounts.reduce((s, a) => s + (a.overdueAmount || 0), 0)
    : serverStats?.totalOverdue ?? 0;
  const totalRecovered =
    recoveries.length > 0
      ? recoveries.reduce((s, r) => s + (r.isReversed ? 0 : r.amount), 0)
      : serverStats?.totalRecovered ?? 0;
  const fileSizeStr = diagnostics?.storageFile?.sizeFormatted || serverStats?.storageFileSizeFormatted || '1.20 MB';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="px-5 py-4 bg-linear-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-500/20 rounded-xl border border-indigo-400/30">
              <Database className="w-5 h-5 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold tracking-tight">Backend Database & Storage Manager</h2>
              <p className="text-xs text-indigo-200/80">
                Centralized Server Storage & Performance Engine (Cloud Run / Node.js)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto space-y-5 flex-1 text-slate-700 text-sm">
          {/* Status Feedback Toast */}
          {syncStatus && (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-indigo-900 text-xs font-semibold flex items-center gap-2 animate-in fade-in">
              <RefreshCw className="w-4 h-4 text-indigo-600 animate-spin shrink-0" />
              <span>{syncStatus}</span>
            </div>
          )}

          {/* Top Server Health Card */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
                <Server className="w-4 h-4" />
              </div>
              <div>
                {/* This said "Active (Cloud Run)" as fixed text: it would
                    have said the same thing with the database unreachable,
                    which is the one moment anybody reads it. It now reports
                    what the Firestore listeners are actually doing. */}
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Database</div>
                <div className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mt-0.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      firestoreSpeedStats?.connected ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
                    }`}
                  />
                  {firestoreSpeedStats?.connected ? 'Connected' : 'Not connected'}
                </div>
              </div>
            </div>

            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100 text-indigo-700">
                <HardDrive className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Backend Data File</div>
                <div className="text-sm font-bold text-slate-800 mt-0.5">
                  {fileSizeStr} on Disk
                </div>
              </div>
            </div>

            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                <Activity className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Last Server Sync</div>
                <div className="text-sm font-bold text-slate-800 mt-0.5">
                  {lastAutoSyncTime || 'Active Now'}
                </div>
              </div>
            </div>
          </div>

          {/* Centralized Records Summary Grid */}
          <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-indigo-600" />
                Backend Master Storage Counts
              </h3>
              <button
                onClick={loadBackendInfo}
                disabled={isLoadingStats}
                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer"
              >
                <RefreshCw className={`w-3 h-3 ${isLoadingStats ? 'animate-spin' : ''}`} />
                Refresh Counts
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-xs">
                <span className="text-[11px] font-medium text-slate-500 block">Total Accounts</span>
                <span className="text-base font-extrabold text-slate-900 block mt-0.5">
                  {totalAccountsCount.toLocaleString()}
                </span>
                <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-0.5 mt-0.5">
                  <Check className="w-3 h-3" /> Central Store
                </span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-xs">
                <span className="text-[11px] font-medium text-slate-500 block">Total Outstanding</span>
                <span className="text-base font-extrabold text-blue-900 block mt-0.5 truncate">
                  {formatINR(totalOutstanding)}
                </span>
                <span className="text-[10px] text-slate-500">Calculated on Server</span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-xs">
                <span className="text-[11px] font-medium text-slate-500 block">Total Overdue</span>
                <span className="text-base font-extrabold text-red-700 block mt-0.5 truncate">
                  {formatINR(totalOverdue)}
                </span>
                <span className="text-[10px] text-slate-500">Auto-aggregated</span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-xs">
                <span className="text-[11px] font-medium text-slate-500 block">Total Recovered</span>
                <span className="text-base font-extrabold text-emerald-700 block mt-0.5 truncate">
                  {formatINR(totalRecovered)}
                </span>
                <span className="text-[10px] text-emerald-600 font-semibold">Net Verified</span>
              </div>
            </div>

            {/* Additional counts */}
            <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-4 text-xs text-slate-600">
              <div>
                <span className="font-bold text-slate-800">{ptps.length}</span> PTP Commitments
              </div>
              <div>
                <span className="font-bold text-slate-800">{visits.length}</span> Field Visits
              </div>
              <div>
                <span className="font-bold text-slate-800">
                  {allocationCount === null ? '…' : allocationCount < 0 ? '—' : allocationCount}
                </span>{' '}
                Agent Allocations
              </div>
              <div>
                <span className="font-bold text-slate-800">{diagnostics?.counts?.users ?? 'Active'}</span> Registered Team Users
              </div>
            </div>
          </div>

          {/* Architecture & Performance Notice */}
          <div className="p-3.5 bg-blue-50/70 border border-blue-200/70 rounded-xl flex items-start gap-2.5 text-xs text-blue-950">
            <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="font-bold text-blue-900">
                Server-Authoritative Data Architecture
              </p>
              <p className="text-blue-800/90 leading-relaxed">
                Accounts and transactions are stored directly in the central backend database (<code className="bg-blue-100/80 px-1 py-0.5 rounded text-[11px]">/data/srms_master_store.json</code>). Heavy calculations (stage breakdowns, totals, commission matching) are processed by backend APIs, freeing your browser CPU and ensuring consistent updates across all devices and logins.
              </p>
            </div>
          </div>

          {/* User-Friendly Backend Action Controls */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Instant Backend Data Actions
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={handlePullData}
                disabled={isSyncing}
                className="p-3 bg-white hover:bg-slate-50 border border-slate-200 hover:border-indigo-300 rounded-xl flex items-center gap-3 transition cursor-pointer text-left group shadow-xs"
              >
                <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition shrink-0">
                  <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-800 group-hover:text-indigo-600 transition">
                    Pull / Reload from Server
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Fetch the authoritative central database from server disk
                  </div>
                </div>
              </button>

              <button
                onClick={handlePushData}
                disabled={isSyncing}
                className="p-3 bg-white hover:bg-slate-50 border border-slate-200 hover:border-emerald-300 rounded-xl flex items-center gap-3 transition cursor-pointer text-left group shadow-xs"
              >
                <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition shrink-0">
                  <UploadCloud className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-800 group-hover:text-emerald-600 transition">
                    Force Push to Backend
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Save current workspace directly into server master storage
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  exportAllBackendData();
                }}
                className="p-3 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-400 rounded-xl flex items-center gap-3 transition cursor-pointer text-left group shadow-xs"
              >
                <div className="p-2 rounded-lg bg-slate-100 text-slate-700 group-hover:bg-slate-800 group-hover:text-white transition shrink-0">
                  <FileSpreadsheet className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-800 group-hover:text-slate-900 transition">
                    Export Multi-Tab Excel Backup
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Download complete snapshot (.xlsx) of all backend tables
                  </div>
                </div>
              </button>

              <button
                onClick={handleClearBrowserCacheOnly}
                className="p-3 bg-white hover:bg-rose-50/60 border border-slate-200 hover:border-rose-300 rounded-xl flex items-center gap-3 transition cursor-pointer text-left group shadow-xs"
              >
                <div className="p-2 rounded-lg bg-rose-50 text-rose-600 group-hover:bg-rose-600 group-hover:text-white transition shrink-0">
                  <Trash2 className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-800 group-hover:text-rose-600 transition">
                    Reset Local Browser Cache
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Purge stuck localStorage and reload clean from backend server
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <span className="text-[11px] text-slate-500">
            Node.js v20 • Express Server • High Capacity JSON Master Storage
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition cursor-pointer"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
